import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, List, ListOrdered,
  Heading1, Heading2, Heading3, AlignLeft, AlignCenter, AlignRight,
  Image as ImageIcon, Link as LinkIcon, Code, Undo, Redo, Palette,
} from "lucide-react";

const COLORS = ["#0f172a", "#00157f", "#0037ff", "#04ccda", "#16a34a", "#dc2626", "#f59e0b", "#64748b", "#ffffff"];

const ToolbarBtn = ({ onClick, active, title, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 transition-colors ${
      active ? "bg-blue-100 text-blue-700" : "text-slate-600"
    }`}
  >
    {children}
  </button>
);

const RichHtmlEditor = ({ value, onChange, "data-testid": testId }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[220px] p-3",
      },
    },
  });

  // Keep external value in sync when switching templates
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link")?.href || "";
    const url = window.prompt("URL del enlace (vacío para quitar)", prev);
    if (url === null) return;
    if (url === "") return editor.chain().focus().unsetMark("link").run();
    editor.chain().focus().extendMarkRange("link").setMark("link", { href: url }).run();
  };

  const insertImage = () => {
    const url = window.prompt("URL de la imagen");
    if (!url) return;
    editor.chain().focus().insertContent(`<img src="${url}" style="max-width:100%;height:auto;" />`).run();
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 bg-slate-50 border-b">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Negrita">
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Cursiva">
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Tachado">
          <Strikethrough className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Código">
          <Code className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="H1">
          <Heading1 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2">
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="H3">
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Lista">
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Lista numerada">
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Izquierda">
          <AlignLeft className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Centro">
          <AlignCenter className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Derecha">
          <AlignRight className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-slate-300 mx-1" />

        {/* Color */}
        <div className="relative group">
          <button type="button" className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Color de texto">
            <Palette className="w-4 h-4" />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-2 hidden group-hover:flex flex-wrap gap-1 z-20 w-[160px]">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => editor.chain().focus().setColor(c).run()}
                className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform"
                style={{ background: c }}
                title={c}
              />
            ))}
          </div>
        </div>

        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Enlace">
          <LinkIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={insertImage} title="Imagen">
          <ImageIcon className="w-4 h-4" />
        </ToolbarBtn>

        <div className="w-px h-6 bg-slate-300 mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Deshacer">
          <Undo className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rehacer">
          <Redo className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} className="bg-white" />
    </div>
  );
};

export default RichHtmlEditor;
